import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
const LOCATION_NOTE = 'locationNote'

const initialState = {
	entities: {},
	ids: [],
	isLoading: false,
	isInitialized: false,
	error: null,
	notes: {
		loading: true,
		data: [],
		error: null,
	},
	noteComments: {
		loading: true,
		data: [],
		error: null,
	},
}

function prepConfig(getState) {
	const {
		userLogin: { userInfo },
	} = getState()

	return {
		headers: {
			Authorization: `Bearer ${userInfo.token}`,
		},
	}
}

export const createNote = createAsyncThunk(
	`${LOCATION_NOTE}/create`,
	async (note, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post(`/api/locationNotes`, note, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to get notes')
		}
	},
)

/**
 * Delete a note and the images it carried.
 *
 * The server decides whether this is allowed — the note's author or the
 * location's owner, and only while the note has no comments. The message it
 * returns explains a refusal, so it is surfaced rather than replaced with
 * something generic.
 */
export const deleteNote = createAsyncThunk(
	`${LOCATION_NOTE}/deleteNote`,
	async (noteId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(
				`/api/locationNotes/${noteId}/delete`,
				{},
				prepConfig(getState),
			)
			return { noteId, ...data }
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message || 'Failed to delete this note.',
			)
		}
	},
)

export const initializeNotes = createAsyncThunk(
	`${LOCATION_NOTE}/initializeNotes`,
	async (payload: any, { getState, rejectWithValue }) => {
		const { locationId, projectId } = payload

		try {
			const { data } = await axios.get(`/api/locationnotes/notes`, {
				params: {
					locationid: locationId,
					projectid: projectId,
				},
				...prepConfig(getState),
			})

			return data
		} catch (error) {
			console.error(error)
			return rejectWithValue('Failed to get notes')
		}
	},
)

export const initializeNoteComments = createAsyncThunk(
	`${LOCATION_NOTE}/initializeNoteComments`,
	async (payload: any, { getState, rejectWithValue }) => {
		const { locationId, projectId } = payload

		try {
			const { data } = await axios.get(`/api/locationnotes/comments`, {
				params: {
					locationid: locationId,
					projectid: projectId,
				},
				...prepConfig(getState),
			})

			return data
		} catch (error) {
			console.error(error)
			return rejectWithValue('Failed to get notes')
		}
	},
)

export const updateNote = createAsyncThunk(
	`${LOCATION_NOTE}/update`,
	async (note: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(`/api/locationNotes/${note._id}`, note, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to create center')
		}
	},
)

const locationNoteSlice = createSlice({
	name: 'locations',
	initialState,
	reducers: {},
	extraReducers: {
		[deleteNote.fulfilled as any]: (state, action) => {
			const { noteId } = action.payload
			delete state.entities[noteId]
			state.ids = (state.ids || []).filter((id: string) => id !== noteId)
		},
		[createNote.pending as any]: state => {
			state.isLoading = true
			state.isInitialized = false
		},
		[createNote.fulfilled as any]: (state, action) => {
			const newNote = action.payload
			state.entities[newNote._id] = newNote
		},

		[initializeNotes.fulfilled as any]: (state, action) => {
			const notes = action.payload

			state.ids = notes?.map((note, index) => {
				state.entities[note._id] = note
				return note._id
			})
			state.isLoading = false
			state.isInitialized = true
		},
		[initializeNotes.pending as any]: state => {
			state.isLoading = true
		},
		[initializeNotes.rejected as any]: (state, action) => {
			state.isLoading = false
			state.isInitialized = true
		},

		[initializeNoteComments.pending as any]: state => {
			state.isLoading = true
		},
		[initializeNoteComments.fulfilled as any]: (state, action) => ({
			...state,
			noteComments: {
				loading: false,
				data: action.payload,
			},
		}),
		[initializeNoteComments.rejected as any]: (state, action) => ({
			...state,
			noteComments: {
				loading: false,
				error: action.error,
			},
		}),

		[updateNote.fulfilled as any]: (state, action) => {
			const updatedNote = action.payload
			state.entities[updatedNote._id] = updatedNote
		},
	} as any,
})

export default locationNoteSlice

// export const { } = locationsSlice.actions;
